import React, {useEffect, useState} from "react";
import {
    Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, TextField
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import {GoogleAuthUser, RoleAssignment} from "../../../server/auth/GoogleAuth";

interface ChapterSettings {
    chapterId: number | ''
    chapterName: string
    website: string
}

export default function ChapterManager() {
    const [chapter, setChapter] = useState<ChapterSettings>({chapterId: '', chapterName: '', website: ''})
    const [users, setUsers] = useState<GoogleAuthUser[]>([])
    const [addOwnerId, setAddOwnerId] = useState('')
    const [saved, setSaved] = useState(false)

    useEffect(() => {
        loadAll()
    }, [])

    function loadAll() {
        axios.get('/admin/chapter').then(({data}) => {
            if (data) setChapter({
                chapterId: data.chapterId ?? '',
                chapterName: data.chapterName ?? '',
                website: data.website ?? ''
            })
        }).catch(() => {})
        axios.get('/admin/user').then(({data}) => setUsers(data)).catch(() => {})
    }

    async function handleSave() {
        await axios.put('/admin/chapter', chapter)
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
    }

    async function handleAddOwner() {
        if (!addOwnerId) return
        await axios.post(`/admin/user/${addOwnerId}/role`, {role: 'Owner'})
        setAddOwnerId('')
        const {data} = await axios.get('/admin/user')
        setUsers(data)
    }

    async function handleRemoveOwner(userId: string, roleId: number) {
        await axios.delete(`/admin/user/${userId}/role/${roleId}`)
        const {data} = await axios.get('/admin/user')
        setUsers(data)
    }

    const owners = users.filter(u => u.roles.some(r => r.role === 'Owner'))
    const ownerIds = new Set(owners.map(u => u.id))
    const nonOwners = users.filter(u => u.authorized && !ownerIds.has(u.id))

    const ownerColumns: GridColDef[] = [
        {field: 'email', headerName: 'Email', flex: 2},
        {field: 'display_name', headerName: 'Display Name', flex: 1},
        {
            field: 'actions', headerName: '', width: 60, sortable: false,
            renderCell: (params) => {
                const ownerRole = (params.row as GoogleAuthUser).roles.find((r: RoleAssignment) => r.role === 'Owner')
                return ownerRole ? (
                    <Button size="small" onClick={() => handleRemoveOwner(params.row.id, ownerRole.id)}>
                        <DeleteIcon fontSize="small"/>
                    </Button>
                ) : null
            }
        }
    ]

    return (
        <Grid container spacing={2} p={1}>
            <Grid item xs={12}>
                <Paper elevation={2} sx={{p: 1}}>
                    <Typography variant="h4" align="center">Chapter Manager</Typography>
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{p: 2}}>
                    <Typography variant="h6" gutterBottom>Chapter Info</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth label="Chapter Number" type="number"
                                value={chapter.chapterId}
                                onChange={e => setChapter(c => ({...c, chapterId: e.target.value === '' ? '' : parseInt(e.target.value)}))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth label="Chapter Name"
                                value={chapter.chapterName}
                                onChange={e => setChapter(c => ({...c, chapterName: e.target.value}))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth label="Website"
                                value={chapter.website}
                                onChange={e => setChapter(c => ({...c, website: e.target.value}))}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" onClick={handleSave} disabled={chapter.chapterId === ''}>
                                {saved ? 'Saved!' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{p: 2}}>
                    <Typography variant="h6" gutterBottom>Chapter Owners</Typography>
                    <DataGrid
                        autoHeight
                        rows={owners}
                        columns={ownerColumns}
                        density="compact"
                        disableRowSelectionOnClick
                        hideFooter={owners.length <= 10}
                        initialState={{pagination: {paginationModel: {pageSize: 10}}}}
                        sx={{mb: 2}}
                    />
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                        <FormControl size="small" sx={{flex: 1}}>
                            <InputLabel>Add Owner</InputLabel>
                            <Select
                                label="Add Owner"
                                value={addOwnerId}
                                onChange={(e: SelectChangeEvent) => setAddOwnerId(e.target.value)}
                            >
                                {nonOwners.map(u => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.display_name ?? u.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" disabled={!addOwnerId} onClick={handleAddOwner}>
                            Add
                        </Button>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    )
}
